import { makeSpring } from './springs.js';
import { BeatDetector, OnsetDetector } from './beats.js';
import { SectionDetector } from './sections.js';

// Per-frame audio orchestrator. Owns springs + detectors and publishes
// a single Signals object the visual layer reads each frame. The actual
// BandAnalyser is injected via setAnalyser() so the orchestrator stays
// independent of the WebAudio source-manager wiring (Task 7).
const EMPTY_SIGNALS = () => ({
  bass: 0, mids: 0, treble: 0,
  lowMids: 0, highMids: 0,
  bassSpring: 0, midsSpring: 0, trebleSpring: 0,
  lowMidsSpring: 0, highMidsSpring: 0,
  beat: false, onset: false, beatPulse: 0, onsetPulse: 0,
  flux: 0, lowEnergy: 0,
  section: 'silence', sectionId: 0, sectionChangedAt: 0,
});

// Target time-constant for lowEnergy EMA: ~30s window. alpha = dt / TAU.
const LOW_ENERGY_TAU_SEC = 30;

export class AudioEngine {
  constructor({ analyser = null, now = () => performance.now() } = {}) {
    this.analyser = analyser;
    this.now = now;

    this.bassSpring = makeSpring(6);
    this.midsSpring = makeSpring(6);
    this.lowMidsSpring = makeSpring(6);
    this.highMidsSpring = makeSpring(6);
    this.trebleSpring = makeSpring(12);
    this.beatPulseSpring = makeSpring(6);
    this.onsetPulseSpring = makeSpring(12);

    this.beatDetector = new BeatDetector();
    this.onsetDetector = new OnsetDetector();
    this.sectionDetector = new SectionDetector();

    this._lowEnergy = 0;
    this.signals = EMPTY_SIGNALS();
    this.status = { source: analyser ? 'unknown' : 'none', error: undefined };
  }

  setAnalyser(analyserLike) {
    this.analyser = analyserLike;
  }

  setSource(name) {
    this.status.source = name;
    if (name && name !== 'none') this.status.error = undefined;
  }

  setError(msg) {
    this.status.error = msg;
  }

  stop() {
    this.analyser = null;
    this.status.source = 'none';
    this.status.error = undefined;
    // Reset accumulators so the next attached source starts fresh.
    this.bassSpring.reset();
    this.midsSpring.reset();
    this.lowMidsSpring.reset();
    this.highMidsSpring.reset();
    this.trebleSpring.reset();
    this.beatPulseSpring.reset();
    this.onsetPulseSpring.reset();
    this._lowEnergy = 0;
    this.signals = EMPTY_SIGNALS();
  }

  update(frameMs) {
    if (!this.analyser || this.status.source === 'none') {
      this.signals = EMPTY_SIGNALS();
      return;
    }
    const dt = frameMs / 1000;
    const { bass, mids, treble, lowMids = 0, highMids = 0, raw } = this.analyser.read();

    this.bassSpring.setTarget(bass);
    this.midsSpring.setTarget(mids);
    this.lowMidsSpring.setTarget(lowMids);
    this.highMidsSpring.setTarget(highMids);
    this.trebleSpring.setTarget(treble);
    const bs = this.bassSpring.update(dt);
    const ms = this.midsSpring.update(dt);
    const lms = this.lowMidsSpring.update(dt);
    const hms = this.highMidsSpring.update(dt);
    const ts = this.trebleSpring.update(dt);

    const nowMs = this.now();

    const instEnergy = bass * 0.6 + mids * 0.4;
    const beat = this.beatDetector.detect(instEnergy, nowMs);
    this.beatPulseSpring.setTarget(beat ? 1 : 0);
    const beatPulse = this.beatPulseSpring.update(dt);

    const onset = this.onsetDetector.detect(raw, nowMs);
    this.onsetPulseSpring.setTarget(onset ? 1 : 0);
    const onsetPulse = this.onsetPulseSpring.update(dt);
    const flux = this.onsetDetector.lastFlux || 0;

    // Slow long-window baseline of bass for ambient floor in the shader.
    const alpha = Math.min(1, dt / LOW_ENERGY_TAU_SEC);
    this._lowEnergy = Math.max(0, Math.min(1, this._lowEnergy + alpha * (bass - this._lowEnergy)));

    const sec = this.sectionDetector.update(bs, ms, ts, nowMs);

    this.signals = {
      bass, mids, treble, lowMids, highMids,
      bassSpring: bs, midsSpring: ms, trebleSpring: ts,
      lowMidsSpring: lms, highMidsSpring: hms,
      beat, onset, beatPulse, onsetPulse,
      flux, lowEnergy: this._lowEnergy,
      section: sec.section,
      sectionId: sec.sectionId,
      sectionChangedAt: sec.sectionChangedAt,
    };
  }
}
